import type { WhatsAppDocType } from '@erp/shared';
import { isAxiosError } from 'axios';
import { MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { getWhatsAppConfigCached, normalizeWhatsappPhone, openWhatsApp, renderWhatsAppMessage, sendWhatsAppMessage } from '../../lib/whatsapp.js';

interface WhatsAppButtonProps {
  docType: WhatsAppDocType;
  /** Customer phone (any format) — normalized to wa.me digits with the tenant dial code. */
  phone: string | null | undefined;
  /** Template placeholders → values (already formatted: money/dates). */
  vars: Record<string, string | undefined>;
  label?: string;
  variant?: 'secondary' | 'ghost' | 'primary';
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * "Send via WhatsApp" — renders the tenant's template for this doc type and sends
 * it, THROUGH A CONFIRMATION STEP.
 *
 * The button used to fire on the click. That is the wrong shape for this action:
 * it goes to a customer from the shop's own number, it cannot be unsent, and
 * until it had already happened nobody could see what it was going to say — a
 * stale placeholder or a missing link first showed up in the customer's chat.
 * So the click now RENDERS the message and shows it, with the number it is bound
 * for, and sending is a second, deliberate press.
 *
 * The text is editable here. It is a message to a person, and the moment you can
 * tell it needs a word changed is the moment you are looking at it. Editing never
 * touches the saved template — Settings → WhatsApp owns that.
 */
export function WhatsAppButton({ docType, phone, vars, label, variant = 'secondary', size, className }: WhatsAppButtonProps): JSX.Element {
  const { t } = useTranslation('whatsapp');
  const { t: tc } = useTranslation();
  const [busy, setBusy] = useState(false);
  /**
   * Non-null while the confirmation is open: the destination, the text, and the
   * tenant's dial code. The code is carried because the number is EDITABLE — a
   * hand-typed local "0750…" has to be normalised again at send time, and the
   * config it needs was fetched when the dialog opened.
   */
  const [draft, setDraft] = useState<{ to: string; text: string; dialCode: string } | null>(null);

  /** Resolve the number, render the template, then hand it to the operator. */
  const onClick = async (): Promise<void> => {
    setBusy(true);
    try {
      const cfg = await getWhatsAppConfigCached();
      // An empty box rather than a refusal when the customer has no number on
      // file: the field is editable, so "no number" is something the operator
      // can fix here instead of being sent away to the customer record.
      const to = normalizeWhatsappPhone(phone, cfg.dialCode) ?? '';
      // renderWhatsAppMessage, not renderTemplate: a stored template with no
      // {link} would otherwise drop `vars.link` on the floor and send a total
      // with no document.
      setDraft({ to, text: renderWhatsAppMessage(cfg.templates[docType] ?? '', vars), dialCode: cfg.dialCode });
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  const confirmSend = async (): Promise<void> => {
    if (!draft) return;
    // Normalise again on the way out: what is in the box is whatever was typed,
    // which may be a local 0-prefixed number or carry spaces and dashes.
    const to = normalizeWhatsappPhone(draft.to, draft.dialCode);
    if (!to) { toast.error(t('badPhone', { defaultValue: 'That is not a number WhatsApp can reach.' })); return; }
    setBusy(true);
    try {
      // Preferred: send from the store's own number via the configured gateway.
      await sendWhatsAppMessage(to, draft.text);
      toast.success(t('sent', { defaultValue: 'Message sent via WhatsApp.' }));
      setDraft(null);
    } catch (err) {
      const code = isAxiosError(err) ? (err.response?.data as { cause?: { code?: string } } | undefined)?.cause?.code : undefined;
      // No gateway configured, or the gateway refused the send (number not on
      // WhatsApp, instance dropped, …) → fall back to opening wa.me so the button
      // always works. Closed either way: from here it is WhatsApp's window, and
      // leaving ours open invites a second send of the same message.
      if (code === 'whatsapp.unconfigured' || code === 'whatsapp.send_failed') {
        openWhatsApp(to, draft.text);
        setDraft(null);
      } else if (code === 'whatsapp.bad_phone') {
        toast.error(t('noPhone', { defaultValue: 'This customer has no phone number.' }));
      } else {
        // Left OPEN on an unknown failure: the text is still there to retry with,
        // and closing would throw away an edit that was just made by hand.
        toast.error(tc('errors:server'));
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* `busy && !draft` so the spinner belongs to the press that is actually
          loading — once the dialog is up, the Send button owns it. */}
      <Button variant={variant} size={size} onClick={onClick} loading={busy && !draft} className={className}>
        <MessageCircle className="h-4 w-4" aria-hidden />
        {label ?? t('send', { defaultValue: 'WhatsApp' })}
      </Button>

      <Dialog open={draft !== null} onOpenChange={(o) => { if (!o && !busy) setDraft(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t('confirmTitle', { defaultValue: 'Send this on WhatsApp?' })}
            </DialogTitle>
            <DialogDescription>
              {t('confirmHintTo', {
                defaultValue: 'Goes to the number below — change it to send this to somebody else.',
              })}
            </DialogDescription>
          </DialogHeader>

          {/* The recipient, editable. It was a line of text, which meant a
              customer whose stored number was wrong could not be reached from
              here at all. dir=ltr so the leading + stays in front of the digits
              in an Arabic admin, where bidi would otherwise throw it to the end. */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-fg-muted">{t('confirmTo', { defaultValue: 'To' })}</span>
            <span className="flex items-center gap-1.5 rounded-md border border-border bg-bg px-2.5 focus-within:border-brand">
              <span className="font-mono text-sm text-fg-muted">+</span>
              <input
                value={draft?.to ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, to: e.target.value } : d))}
                dir="ltr"
                inputMode="tel"
                autoComplete="tel"
                placeholder="9647501234567"
                className="h-9 w-full bg-transparent font-mono text-sm text-fg outline-none"
                aria-label={t('confirmTo', { defaultValue: 'To' })}
              />
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-fg-muted">{t('preview', { defaultValue: 'Message' })}</span>
            <textarea
              value={draft?.text ?? ''}
              onChange={(e) => setDraft((d) => (d ? { ...d, text: e.target.value } : d))}
              rows={9}
              dir="auto"
              className="w-full resize-y rounded-md border border-border bg-bg p-2.5 text-sm text-fg outline-none focus:border-brand"
              aria-label={t('preview', { defaultValue: 'Message' })}
            />
          </label>
          <p className="text-[11px] text-fg-subtle">
            {t('confirmHint', { defaultValue: 'Edit it if you like — this changes only the message being sent, not your saved template.' })}
          </p>

          <div className="mt-2 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)} disabled={busy}>
              {tc('actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={() => void confirmSend()} loading={busy} disabled={!draft?.text.trim() || !draft?.to.trim()}>
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t('send', { defaultValue: 'WhatsApp' })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
